import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { clientApi } from '@/services/api'
import toast from 'react-hot-toast'
import { Check } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

export default function CreateClient() {
  const { currentFirm } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    name: '',
    entity_type: 'company',
    registration_no: '',
    country: '',
    currency: 'USD',
    financial_year_end: 12,
    plan_tier: 'starter',
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!currentFirm) {
      toast.error('No firm found. Please create a firm first from the Dashboard.')
      navigate('/')
      return
    }
    setLoading(true)
    try {
      await clientApi.create({ ...form, firm_id: currentFirm.id })
      toast.success('Client created')
      navigate('/clients')
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create client'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">New Client</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Set up a new client ledger</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Client Name */}
            <div className="space-y-1.5">
              <Label>Client Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Acme Holdings Ltd"
                required
              />
            </div>

            {/* Entity + Registration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Entity Type</Label>
                <select
                  value={form.entity_type}
                  onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                  className="input"
                >
                  <option value="company">Company</option>
                  <option value="sole_trader">Sole Trader</option>
                  <option value="trust">Trust</option>
                  <option value="partnership">Partnership</option>
                  <option value="nonprofit">Non-Profit</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Registration No.</Label>
                <Input
                  value={form.registration_no}
                  onChange={(e) => setForm({ ...form, registration_no: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Currency + FY End */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="input"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="LKR">LKR - Sri Lankan Rupee</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="SGD">SGD - Singapore Dollar</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Financial Year End</Label>
                <select
                  value={form.financial_year_end}
                  onChange={(e) => setForm({ ...form, financial_year_end: parseInt(e.target.value) })}
                  className="input"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Plan */}
            <div className="space-y-2">
              <Label>Subscription Plan</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'starter', name: 'Starter', price: '$5', desc: 'Core ledger, bank import, reconciliation, journals, reports' },
                  { value: 'premium', name: 'Premium', price: '$10', desc: 'Everything in Starter + invoicing, fixed assets, budgets' },
                ].map((plan) => {
                  const selected = form.plan_tier === plan.value
                  return (
                    <label
                      key={plan.value}
                      className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all ${
                        selected
                          ? 'border-[#0078C8] bg-[#0078C8]/5 ring-1 ring-[#0078C8]/20'
                          : 'border-border hover:border-[rgba(0,10,30,0.3)]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="plan"
                        value={plan.value}
                        checked={selected}
                        onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}
                        className="sr-only"
                      />
                      {selected && (
                        <div className="absolute top-3 right-3 w-5 h-5 bg-[#0078C8] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-[15px] font-bold text-[#000A1E]">{plan.name}</span>
                        <span className="text-[13px] text-[rgba(0,10,30,0.75)]">{plan.price}/client/mo</span>
                      </div>
                      <p className="text-[13px] text-[rgba(0,10,30,0.75)] leading-relaxed">{plan.desc}</p>
                    </label>
                  )
                })}
              </div>
            </div>

            <Separator />

            <div className="flex gap-3">
              <Button type="submit" variant="create" disabled={loading}>
                {loading ? 'Creating...' : 'Create Client'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/clients')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
